import { type ID, selectOwnItems } from '@octopus-do/octopus-project-core'
import { selectCanEditLibrary } from '@store/librarySaga'
import { thunkAddLibrarySymbol } from '@store/migrationActions/thunkActions/thunkSymbolActions'
import type { AppState } from '@store/types'
import type React from 'react'
import { useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import type { Action } from 'redux'
import type { ThunkDispatch } from 'redux-thunk'

import { GtmTriggerName, sendGtmEvent } from '@/constants/gtm'
import { Empty } from '../Empty'
import { SymbolsPage } from '../SymbolsPage'
import { LibrarySymbol } from './components/LibrarySymbol'

const selectOwnSymbols = selectOwnItems('symbol')

/**
 * The symbols of the workspace of the project: the same list as the project
 * one, only shared. A new symbol is created right here and named at once.
 */
export const LibrarySymbols: React.FC = () => {
  const dispatch = useDispatch<ThunkDispatch<AppState, unknown, Action>>()

  const [selectedId, setSelectedId] = useState<ID | null>(null)
  const [editingId, setEditingId] = useState<ID | null>(null)

  const items = useSelector(selectOwnSymbols, shallowEqual)
  const canEdit = useSelector(selectCanEditLibrary)

  const onSelect = (id: ID | null) => {
    setSelectedId(id)
    setEditingId(null)
  }

  const onAdd = async () => {
    sendGtmEvent(
      GtmTriggerName.SYMBOL_ADD_SIDEBAR,
      'Click to create symbol in the library'
    )

    const id = await dispatch(thunkAddLibrarySymbol())

    if (id) {
      setSelectedId(id)
      setEditingId(id)
    }
  }

  if (items.length === 0) {
    return <Empty onCreate={onAdd} />
  }

  return (
    <SymbolsPage onAdd={onAdd}>
      {items.map((item, index) => (
        <LibrarySymbol
          key={item.id}
          item={item}
          index={index}
          overlay={false}
          selected={selectedId === item.id}
          editing={editingId === item.id}
          canEdit={canEdit}
          onSelect={onSelect}
          onEdit={setEditingId}
        />
      ))}
    </SymbolsPage>
  )
}
